<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  href: {
    type: String,
    default: null
  },
  to: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: 'flex'
  },
  activeColor: {
    type: String,
    default: 'text-brand-600 dark:text-brand-400'
  },
  hasDivider: Boolean,
  isDesktopIconOnly: Boolean,
  dropdown: Boolean,
  active: Boolean
})

const root = ref(null)

const is = computed(() => {
  if (props.href) {
    return 'a'
  }

  if (props.to) {
    return 'router-link'
  }

  return 'div'
})

// A plain <div> that reacts to clicks still has to be reachable by keyboard.
const isButtonLike = computed(() => is.value === 'div' && !props.dropdown)

const componentClass = computed(() => {
  const base = [
    props.type,
    'items-center',
    'justify-center',
    'flex-grow-0',
    'flex-shrink-0',
    'relative',
    'cursor-pointer',
    'rounded-xl',
    'transition-colors',
    'duration-150',
    'focus:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-brand-500',
    props.active
      ? props.activeColor
      : 'text-gray-500 hover:text-brand-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-brand-400 dark:hover:bg-gray-700'
  ]

  if (props.type === 'block') {
    base.push('lg:flex')
  }

  if (!props.dropdown) {
    base.push('p-2')
  } else {
    base.push('p-0', 'lg:p-2')
  }

  if (props.hasDivider) {
    base.push('lg:border-r', 'lg:border-gray-200', 'lg:dark:border-gray-700')
  }

  if (props.isDesktopIconOnly) {
    base.push('lg:w-16', 'lg:justify-center')
  }

  return base
})

const activeClass = computed(() => {
  return is.value === 'router-link' ? props.activeColor : null
})

const onKeydown = event => {
  if (!isButtonLike.value) {
    return
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    root.value?.click?.()
  }
}
</script>

<template>
  <component
    :is="is"
    ref="root"
    :class="componentClass"
    :to="to"
    :href="href"
    :exact-active-class="activeClass"
    :role="isButtonLike ? 'button' : null"
    :tabindex="isButtonLike ? 0 : null"
    @keydown="onKeydown"
  >
    <slot />
  </component>
</template>
